/**
 * useVideoControls — player-event-driven state for the custom chrome bar.
 *
 * Intentionally separate from the playback Recoil atoms in ../../State.ts:
 *   - Recoil state    → drives intro/midclip/endcard transitions across the
 *                       whole branded view (banner timing, intro overlay, etc).
 *   - This local state → drives the chrome UI (play icon vs pause icon,
 *                       progress thumb position, mute toggle, fullscreen).
 *
 * Both stay in sync via the same video.js player — the parent pumps Recoil
 * from the player's timeupdate; we listen to the same event for the chrome.
 * Splitting them avoids re-rendering all 20 PlaylistRow rows every animation
 * frame just because the chrome thumb moved.
 *
 * Everything binds to the video.js Player (from useVideoJsVideo), NOT the
 * React-rendered <video> ref: on iOS video.js clones the element it adopts
 * (Html5.movingMediaElementInDOM is false there) and plays through the clone,
 * so listeners/commands on the original element silently no-op — that bug
 * shipped as a frozen 0:00-0:00 scrubber and a dead play button on iPhone.
 */
import { RefObject, useCallback, useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import type videojs from "video.js";

import { BrandedFullscreenState } from "../../State";

interface VideoControls {
  playing: boolean;
  muted: boolean;
  /** Player volume, 0..1. Independent of `muted` (muting preserves volume). */
  volume: number;
  currentSec: number;
  durationSec: number;
  togglePlay: () => void;
  toggleMute: () => void;
  /** Set volume from a 0..1 fraction. 0 mutes; anything above unmutes. */
  setVolume: (fraction: number) => void;
  seekToFraction: (fraction: number) => void;
  toggleFullscreen: () => void;
}

export const useVideoControls = (
  playerRef: RefObject<videojs.Player | null>,
  /**
   * A value that changes whenever the parent expects the listener set to
   * re-attach — typically the active clip ID. Necessary because `playerRef`
   * itself is a stable RefObject; the player is created lazily once the
   * <video> mounts, so an effect with `[playerRef]` deps alone could run
   * before it exists and never again.
   */
  reattachKey?: unknown,
): VideoControls => {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolumeState] = useState(1);
  const [currentSec, setCurrentSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const setFullscreen = useSetRecoilState(BrandedFullscreenState);

  // Track browser fullscreen state on the document so overlays
  // (watermark, top banner, endcard title) can scale up dramatically
  // when the player goes full-viewport. CSS :fullscreen alone proved
  // unreliable inside the iframe context, so we drive the visible
  // changes from React state. The webkit-prefixed event/property cover
  // older iPad Safari (< 16.4), which never fires the unprefixed pair.
  useEffect(() => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
    };
    const handler = () =>
      setFullscreen(!!(doc.fullscreenElement || doc.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    // Initial sync in case we mount while already in fullscreen.
    handler();
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
    };
  }, [setFullscreen]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return undefined;

    // duration() is NaN before metadata arrives and can read Infinity while
    // an HLS manifest is still settling — clamp both so the chrome shows
    // 0:00 instead of "Infinity:NaN", and the thumb math stays sane.
    const readDuration = () => {
      const d = player.duration();
      return Number.isFinite(d) ? d : 0;
    };

    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    const handleTime = () => setCurrentSec(player.currentTime());
    const handleMeta = () => setDurationSec(readDuration());
    const handleVol = () => {
      setMuted(player.muted());
      setVolumeState(player.volume());
    };

    // Initial sync — the listeners only fire on changes, but the player may
    // already have a duration / muted / volume state by the time we attach.
    handleVol();
    setPlaying(!player.paused());
    setCurrentSec(player.currentTime());
    setDurationSec(readDuration());

    player.on("play", handlePlay);
    player.on("pause", handlePause);
    player.on("timeupdate", handleTime);
    player.on("loadedmetadata", handleMeta);
    player.on("durationchange", handleMeta);
    player.on("volumechange", handleVol);
    // muted() misreports as false until the tech attaches (techGet_ returns
    // undefined pre-ready), and an attach that sets muted to its existing
    // value fires no volumechange — re-baseline on loadstart, which is
    // guaranteed once per source load after the tech is ready.
    player.on("loadstart", handleVol);

    return () => {
      // Unmount disposes the player (useVideoJsVideo cleanup runs first),
      // which removes all listeners itself — off() is only for the live
      // re-attach on clip swap.
      if (player.isDisposed()) return;
      player.off("play", handlePlay);
      player.off("pause", handlePause);
      player.off("timeupdate", handleTime);
      player.off("loadedmetadata", handleMeta);
      player.off("durationchange", handleMeta);
      player.off("volumechange", handleVol);
      player.off("loadstart", handleVol);
    };
    // Re-attach when the parent signals via reattachKey (typically
    // activeClip._id), which is what handles the initial mount-after-fetch
    // case and keeps the sync fresh across clip swaps.
  }, [playerRef, reattachKey]);

  // Commands below no-op without a live player — null until the <video>
  // mounts and gone again once the unmount dispose has run.
  const getLivePlayer = useCallback(() => {
    const player = playerRef.current;
    return player && !player.isDisposed() ? player : null;
  }, [playerRef]);

  const togglePlay = useCallback(() => {
    const player = getLivePlayer();
    if (!player) return;
    if (player.paused()) {
      const p = player.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          /* autoplay may reject without user gesture; ignore */
        });
      }
    } else {
      player.pause();
    }
  }, [getLivePlayer]);

  const toggleMute = useCallback(() => {
    const player = getLivePlayer();
    if (!player) return;
    player.muted(!player.muted());
  }, [getLivePlayer]);

  const setVolume = useCallback(
    (fraction: number) => {
      const player = getLivePlayer();
      if (!player) return;
      const clamped = Math.max(0, Math.min(1, fraction));
      player.volume(clamped);
      // Dragging the slider is an explicit volume intent: any non-zero level
      // unmutes, dragging to the bottom mutes. Setting these fires
      // `volumechange`, which syncs `muted`/`volume` state back to the chrome.
      player.muted(clamped === 0);
    },
    [getLivePlayer],
  );

  const seekToFraction = useCallback(
    (fraction: number) => {
      const player = getLivePlayer();
      if (!player) return;
      const duration = player.duration();
      // No (finite) duration yet — nowhere meaningful to seek.
      if (!Number.isFinite(duration) || duration <= 0) return;
      const clamped = Math.max(0, Math.min(1, fraction));
      player.currentTime(clamped * duration);
    },
    [getLivePlayer],
  );

  const toggleFullscreen = useCallback(() => {
    const player = getLivePlayer();
    if (!player) return;
    // Fullscreen the whole player frame (video + sponsor/intro/endcard
    // overlays), NOT the video.js wrapper — the wrapper holds only the
    // <video>, so fullscreening it would drop every overlay.
    const playerEl = player.el();
    if (!playerEl) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => void;
    };
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      if (doc.exitFullscreen) doc.exitFullscreen().catch(() => undefined);
      else doc.webkitExitFullscreen?.();
      return;
    }
    const target = (playerEl.closest("[data-branded-frame]") ??
      playerEl) as HTMLElement & { webkitRequestFullscreen?: () => void };
    if (target.requestFullscreen) {
      target.requestFullscreen().catch(() => undefined);
    } else if (target.webkitRequestFullscreen) {
      // Older WebKit (iPad Safari < 16.4) only ships the prefixed API.
      target.webkitRequestFullscreen();
    } else {
      // iPhone Safari has no element fullscreen API at all — the only
      // fullscreen it offers is the native video player, entered through
      // the media element itself. Branded overlays/chrome can't come
      // along; that's the platform ceiling (YouTube embeds hit the same).
      const tech = playerEl.querySelector("video") as
        | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
        | null;
      try {
        tech?.webkitEnterFullscreen?.();
      } catch {
        /* not ready yet (no metadata) — ignore */
      }
    }
  }, [getLivePlayer]);

  return {
    playing,
    muted,
    volume,
    currentSec,
    durationSec,
    togglePlay,
    toggleMute,
    setVolume,
    seekToFraction,
    toggleFullscreen,
  };
};
